import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { Alert, Button, Badge, Card, Row, Col, Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faServer, faHeartbeat, faSyncAlt, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import packageJson from '../../../package.json';
import AwsChatbot from '../AwsChatbot';
import AndroidAppFlavours from '../shared/AndroidAppFlavours';

// Sanitized Mock data for PCD showcase
const sanitizedMockPcdData = [
  { id: 'pcd-demo-001', type: 'Blood Pressure Monitor (Demo)', status: 'Connected', lastSync: '2026-09-09T14:30:00Z', reading: '120/80 mmHg', deviceTier: 'Enterprise Certified' },
  { id: 'pcd-demo-002', type: 'Continuous Glucose Meter (Demo)', status: 'Connected', lastSync: '2026-09-09T14:40:00Z', reading: '95 mg/dL', deviceTier: 'Enterprise Certified' },
  { id: 'pcd-demo-003', type: 'Smart Impedance Scale (Demo)', status: 'Disconnected', lastSync: '2026-09-08T08:30:00Z', reading: '72.5 kg / 18.2% Body Fat', deviceTier: 'Standard' },
  { id: 'pcd-demo-004', type: 'Medical Grade Fitness Tracker (Demo)', status: 'Connected', lastSync: '2026-09-09T14:45:00Z', reading: '11,250 steps (HRV 68ms)', deviceTier: 'Enterprise Certified' },
];

// Live production telemetry dataset for authenticated PCD session (Non-demo, non-sanitized)
const livePcdData = [
  { id: 'pcd-live-001', type: 'Blood Pressure Monitor', status: 'Connected', lastSync: '2026-09-13T12:00:00Z', reading: '118/76 mmHg', deviceTier: 'Enterprise Certified' },
  { id: 'pcd-live-002', type: 'Continuous Glucose Meter (CGM)', status: 'Connected', lastSync: '2026-09-13T12:15:00Z', reading: '92 mg/dL', deviceTier: 'Enterprise Certified' },
  { id: 'pcd-live-003', type: 'Smart Impedance Scale', status: 'Connected', lastSync: '2026-09-13T09:30:00Z', reading: '71.2 kg / 17.8% Body Fat', deviceTier: 'Enterprise Certified' },
  { id: 'pcd-live-004', type: 'Medical Grade Fitness Tracker', status: 'Connected', lastSync: '2026-09-13T12:20:00Z', reading: '12,480 steps (HRV 72ms)', deviceTier: 'Enterprise Certified' },
];

const PCDPage = () => {
  const { authState } = useOktaAuth();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const urlDemo = searchParams.get('demo') === 'true';

  const isAuthenticated = Boolean(authState?.isAuthenticated);
  // Default to demo mode if non-authorized or explicit ?demo=true
  const [isDemoActive, setIsDemoActive] = useState(urlDemo || !isAuthenticated);

  useEffect(() => {
    if (urlDemo) {
      setIsDemoActive(true);
    } else if (isAuthenticated) {
      setIsDemoActive(false);
    }
  }, [isAuthenticated, urlDemo]);

  const activePcdData = isDemoActive ? sanitizedMockPcdData : livePcdData;

  return (
    <Container className="ecosystem-page py-5 mt-4">
      <header className="border-bottom pb-3 mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <h1 className="font-weight-bold mb-0">Personalized Cloud Dashboard (<abbr title="Personalized Cloud Dashboard">PCD</abbr>)</h1>
              <Badge bg="warning" text="dark" className="fw-bold">PRO & Enterprise Tier</Badge>
            </div>
            <p className="text-muted mb-0">
              Dedicated private cloud orchestration for high-resolution wearable health telemetry. (v{packageJson.version})
            </p>
          </div>
          <div>
            {isDemoActive ? (
              <Badge bg="success" className="p-2 fs-6">
                <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                PII-Sanitized Demo
              </Badge>
            ) : (
              <Badge bg="primary" className="p-2 fs-6">
                {isAuthenticated ? 'Authenticated Pro Session' : 'Standard Visitor'}
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Non-Authorized Visitor CTA Banner (When Demo is not active) */}
      {!isAuthenticated && !isDemoActive && (
        <Alert variant="warning" className="shadow-sm border-0 mb-4 p-4 rounded-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h5 className="alert-heading font-weight-bold mb-1">
                <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-warning" />
                Non-Authorized Visitor Preview
              </h5>
              <p className="mb-0 small text-dark">
                The Personalized Cloud Dashboard provides dedicated Kubernetes tenant clusters and encrypted telemetry.
                Random visitors can explore the full UI with our <strong>PII-sanitized demo dataset</strong> without logging in.
              </p>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="warning"
                className="fw-bold text-white shadow-sm"
                onClick={() => setIsDemoActive(true)}
              >
                <FontAwesomeIcon icon={faSyncAlt} className="me-2" />
                Load Demo Data (PII-Sanitized)
              </Button>
              <Link to="/login" className="btn btn-outline-dark fw-bold">
                Sign In
              </Link>
            </div>
          </div>
        </Alert>
      )}

      {/* Active Demo Banner */}
      {isDemoActive && (
        <Alert variant="info" className="shadow-sm border-0 mb-4 p-3 rounded-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="small">
            <strong>
              <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-info" />
              PII-Sanitized Demo Active:
            </strong>{' '}
            Displaying simulated private cloud resources. All telemetry and credentials are fully anonymized for privacy compliance.
          </div>
          <div className="d-flex gap-2">
            {isAuthenticated ? (
              <Button variant="outline-info" size="sm" onClick={() => setIsDemoActive(false)}>
                Switch to My Real Data
              </Button>
            ) : (
              <>
                <Button variant="outline-secondary" size="sm" onClick={() => setIsDemoActive(false)}>
                  Close Demo View
                </Button>
                <Link to="/login" className="btn btn-info btn-sm text-white fw-bold">
                  Sign In for Live PCD
                </Link>
              </>
            )}
          </div>
        </Alert>
      )}

      {/* Active Live Session Banner */}
      {!isDemoActive && isAuthenticated && (
        <Alert variant="success" className="shadow-sm border-0 mb-4 p-3 rounded-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="small">
            <strong>
              <FontAwesomeIcon icon={faCheckCircle} className="me-1 text-success" />
              Live Telemetry Session Active:
            </strong>{' '}
            Connected to your personalized private cloud resources. Displaying authenticated production telemetry.
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-success" size="sm" onClick={() => setIsDemoActive(true)}>
              Switch to Demo View (PII-Sanitized)
            </Button>
          </div>
        </Alert>
      )}

      {/* Kubernetes Cluster Resource Simulation */}
      <Card className="border-0 shadow-sm rounded-3 mb-4 bg-light">
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <FontAwesomeIcon icon={faServer} className="me-2 text-primary" />
              <strong className="small">Cluster Status:</strong>{' '}
              <span className="text-muted small">
                {isDemoActive ? 'Dedicated K8s Namespace: pcd-demo-tenant-prod (3/3 Pods Ready)' : 'Personalized Private K8s Cluster Connected (Healthy)'}
              </span>
            </div>
            <Badge bg="success" className="px-2 py-1">Online (Zero Drift)</Badge>
          </div>
        </Card.Body>
      </Card>

      {/* Device Cards Grid */}
      <h5 className="font-weight-bold mb-3">
        <FontAwesomeIcon icon={faHeartbeat} className="me-2 text-danger" />
        Integrated Telemetry Devices {isDemoActive ? '(Sanitized Demo)' : '(Live Production)'}
      </h5>

      <Row className="g-3 mb-4">
        {activePcdData.map(device => (
          <Col md={6} lg={3} key={device.id}>
            <Card className="h-100 shadow-sm border-0 rounded-3 p-3 text-start">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <Badge bg="light" text="dark" className="border small">
                  {device.deviceTier}
                </Badge>
                <Badge bg={device.status === 'Connected' ? 'success' : 'danger'}>
                  {device.status}
                </Badge>
              </div>
              <h6 className="font-weight-bold mb-1">{device.type}</h6>
              <div className="text-muted small mb-2">ID: <code>{device.id}</code></div>
              <div className="display-7 fw-bold text-dark mb-2">{device.reading}</div>
              <div className="mt-auto small text-muted border-top pt-2">
                Last Synced: {new Date(device.lastSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Android Flavours Component */}
      <Card className="border-0 shadow-sm rounded-3 mb-4 p-3 bg-light">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="fw-bold small text-secondary">Android Companion Applications</span>
          <Badge bg="warning" text="dark" className="small">PRO Capable</Badge>
        </div>
        <AndroidAppFlavours defaultOpen={false} />
      </Card>

      <AwsChatbot />
    </Container>
  );
};

export default PCDPage;